/**
 * 2026-05-25 — local-only demo unified-sentiment gauge used in the
 * experimental crypto-price-style market view to iterate on the
 * sentiment UX. Pattern: 0–100 score + categorical label
 * (Extremely Bearish → Extremely Bullish) + signal-source
 * breakdown (trade flow / post sentiment / news sentiment) plus
 * 24h message volume. Hardcoded fixture; no API call. Replace
 * with the real <UnifiedSentimentScore/> wired to a populated
 * dataset once the UX is locked.
 */
"use client";

import { useState } from "react";
import { TrendingUp, MessageSquare, Newspaper } from "lucide-react";

const SCORE = 62;
const MESSAGES_24H = 184;

function bullBearLabel(score: number): { text: string; color: string } {
  if (score >= 80) return { text: "Extremely Bullish", color: "#01A04A" };
  if (score >= 60) return { text: "Bullish", color: "#04A858" };
  if (score >= 40) return { text: "Neutral", color: "#5B616E" };
  if (score >= 20) return { text: "Bearish", color: "#C73C46" };
  return { text: "Extremely Bearish", color: "#CF202F" };
}

function activityLabel(score: number): { text: string; color: string } {
  if (score >= 80) return { text: "Very active", color: "#01A04A" };
  if (score >= 60) return { text: "Active", color: "#04A858" };
  if (score >= 40) return { text: "Steady", color: "#5B616E" };
  if (score >= 20) return { text: "Quiet", color: "#7a4a4a" };
  return { text: "Very quiet", color: "#5B616E" };
}

const BREAKDOWN = [
  { label: "Trade flow", value: 68, weight: "60%", icon: TrendingUp },
  { label: "Posts", value: 58, weight: "20%", icon: MessageSquare },
  { label: "News", value: 54, weight: "20%", icon: Newspaper },
] as const;

interface DemoSentimentGaugeProps {
  kind?: "binary" | "multi_outcome";
  topMover?: {
    name: string;
    deltaCents: number;
    icon?: React.ReactNode;
  } | null;
}

export function DemoSentimentGauge({
  kind = "binary",
  topMover = null,
}: DemoSentimentGaugeProps = {}) {
  const isMulti = kind === "multi_outcome";
  const labeller = isMulti ? activityLabel : bullBearLabel;
  const { text: scoreLabel, color: scoreColor } = labeller(SCORE);
  const [expanded, setExpanded] = useState(false);

  const barLabels = isMulti
    ? { left: "Quiet", center: "Steady", right: "Active" }
    : { left: "Bearish", center: "Neutral", right: "Bullish" };
  const heading = isMulti ? "Market momentum" : "Market sentiment";

  return (
    <div className="text-[14px]" style={{ color: "#0A0B0D" }}>
      <div className="mb-4 flex items-center justify-between">
        <h2 className="text-[20px] font-semibold tracking-[-0.01em]">
          {heading}
        </h2>
        <span className="text-[12px]" style={{ color: "#8A919E" }}>
          {MESSAGES_24H} signals · 24h
        </span>
      </div>

      {/* Score + label row. For multi-outcome the top-mover (if
          provided) is shown alongside the score so users know which
          outcome the momentum is concentrated on. */}
      <div className="mb-5 flex items-end gap-4">
        <div
          className="text-[48px] font-semibold leading-none tracking-[-0.02em] tabular-nums"
          style={{ color: scoreColor }}
        >
          {SCORE}
        </div>
        <div className="pb-1">
          <div className="text-[16px] font-semibold" style={{ color: scoreColor }}>
            {scoreLabel}
          </div>
          <div className="text-[12px]" style={{ color: "#5B616E" }}>
            out of 100
          </div>
        </div>
        {isMulti && topMover && (
          <div className="ml-auto pb-1 text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.08em]" style={{ color: "#8A919E" }}>
              Top mover
            </div>
            <div className="mt-0.5 inline-flex items-center gap-1.5 text-[13px] font-semibold" style={{ color: "#0A0B0D" }}>
              {topMover.icon}
              <span>{topMover.name}</span>
              <span
                style={{
                  color: topMover.deltaCents >= 0 ? "#01A04A" : "#CF202F",
                }}
              >
                {topMover.deltaCents >= 0 ? "▲" : "▼"} {Math.abs(topMover.deltaCents)}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Gradient gauge bar */}
      <div className="relative mb-5">
        <div
          className="h-2 w-full overflow-hidden rounded-full"
          style={{
            background:
              "linear-gradient(to right, #CF202F 0%, #E0917B 30%, #ECEFF2 50%, #8FD0A8 70%, #01A04A 100%)",
          }}
          aria-hidden="true"
        />
        <div
          className="absolute -top-1.5 h-5 w-1 -translate-x-1/2 rounded-full"
          style={{
            left: `${SCORE}%`,
            background: "#0A0B0D",
            boxShadow: "0 1px 4px rgba(15, 23, 42, 0.25)",
          }}
          aria-hidden="true"
        />
        <div
          className="mt-2 flex justify-between text-[10px] font-medium uppercase tracking-[0.06em]"
          style={{ color: "#8A919E" }}
        >
          <span>{barLabels.left}</span>
          <span>{barLabels.center}</span>
          <span>{barLabels.right}</span>
        </div>
      </div>

      {/* Signal breakdown — fully collapsed by default. Click
          "More details" to reveal the Composition header + the
          breakdown rows. */}
      <div
        className="border-t pt-4"
        style={{ borderColor: "#ECEFF2" }}
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="inline-flex items-center gap-1 text-[12px] font-semibold underline-offset-2 hover:underline"
          style={{ color: "#5B616E" }}
        >
          {expanded ? "Show less" : "More details"}
          <span
            aria-hidden="true"
            className="inline-block transition-transform"
            style={{
              transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            }}
          >
            ▾
          </span>
        </button>
        <div
          className="mt-4 overflow-hidden transition-[max-height] duration-300 ease-out"
          style={{
            maxHeight: expanded ? "800px" : "72px",
            WebkitMaskImage: expanded
              ? "none"
              : "linear-gradient(to bottom, black 0%, transparent 100%)",
            maskImage: expanded
              ? "none"
              : "linear-gradient(to bottom, black 0%, transparent 100%)",
          }}
        >
          <div
            className="mb-3 text-[12px] font-semibold uppercase tracking-[0.08em]"
            style={{ color: "#0A0B0D" }}
          >
            Composition
          </div>
          <ul className="flex flex-col gap-3">
            {BREAKDOWN.map(({ label, value, weight, icon: Icon }) => (
              <li key={label} className="flex items-center gap-3">
                <span
                  className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full"
                  style={{ background: "#F7F8FA" }}
                >
                  <Icon size={13} strokeWidth={1.75} style={{ color: "#5B616E" }} />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-[13px] font-medium" style={{ color: "#0A0B0D" }}>
                      {label}
                    </span>
                    <span
                      className="text-[12px] tabular-nums"
                      style={{ color: "#5B616E" }}
                    >
                      {value} · {weight}
                    </span>
                  </div>
                  <div
                    className="mt-1 h-1 w-full overflow-hidden rounded-full"
                    style={{ background: "#ECEFF2" }}
                    aria-hidden="true"
                  >
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${value}%`,
                        background: labeller(value).color,
                      }}
                    />
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}
